import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import { usageConversationsResponseSchema, usageSummaryResponseSchema } from '../../lib/schemas.js';
import {
  USAGE_PERIODS,
  type UsageConversationsResponse,
  type UsagePeriodPreset,
  type UsageSummaryResponse,
} from '../../lib/types.js';

/** A preset period, or a custom range of "YYYY-MM-DD" days in the tenant's zone. */
export type UsagePeriod =
  | { kind: 'preset'; period: UsagePeriodPreset }
  | { kind: 'custom'; from: string; to: string };

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const DEFAULT_PRESET: UsagePeriodPreset = '30d';

/** The period in the URL: `?period=7d`, or `?from=…&to=…`; anything else is the 30-day default. */
export const periodFromParams = (params: URLSearchParams): UsagePeriod => {
  const from = params.get('from');
  const to = params.get('to');
  if (from && to && DAY_KEY.test(from) && DAY_KEY.test(to)) return { kind: 'custom', from, to };
  const preset = USAGE_PERIODS.find((p) => p === params.get('period'));
  return { kind: 'preset', period: preset ?? DEFAULT_PRESET };
};

/** How many of the most expensive conversations the page lists. */
export const CONVERSATION_ROWS = 5;

/** The query string both usage endpoints take for a period. */
export const periodQuery = (p: UsagePeriod): string =>
  p.kind === 'preset'
    ? `period=${p.period}`
    : `from=${encodeURIComponent(p.from)}&to=${encodeURIComponent(p.to)}`;

export interface UsageLogic {
  period: UsagePeriod;
  setPeriod: (p: UsagePeriod) => void;
  isLoading: boolean;
  data: UsageSummaryResponse | undefined;
  error: Error | null;
  conversations: UsageConversationsResponse | undefined;
  conversationsError: Error | null;
}

export const useUsageLogic = (): UsageLogic => {
  // In the URL so a period is a shareable link; the default stays out of it.
  const [params, setParams] = useSearchParams();
  const period = periodFromParams(params);
  const setPeriod = (p: UsagePeriod) => {
    setParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        for (const key of ['period', 'from', 'to']) next.delete(key);
        if (p.kind === 'custom') {
          next.set('from', p.from);
          next.set('to', p.to);
        } else if (p.period !== DEFAULT_PRESET) next.set('period', p.period);
        return next;
      },
      { replace: true },
    );
  };
  const qs = periodQuery(period);
  const summary = useQuery<UsageSummaryResponse>({
    queryKey: queryKeys.usage(qs),
    queryFn: () =>
      apiRequest<UsageSummaryResponse>(`/api/usage?${qs}`, undefined, usageSummaryResponseSchema),
  });
  const conversations = useQuery<UsageConversationsResponse>({
    queryKey: queryKeys.usageConversations(qs),
    queryFn: () =>
      apiRequest<UsageConversationsResponse>(
        `/api/usage/conversations?${qs}&limit=${String(CONVERSATION_ROWS)}`,
        undefined,
        usageConversationsResponseSchema,
      ),
  });

  return {
    period,
    setPeriod,
    isLoading: summary.isLoading,
    data: summary.data,
    error: summary.error,
    conversations: conversations.data,
    conversationsError: conversations.error,
  };
};
